import { FormikProps, Field } from "formik";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Flex,
  Button,
  Heading,
  Icon,
  Divider,
  InputGroup,
  Select,
} from "@chakra-ui/react";
import { MdHome, MdSearch } from "react-icons/md";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FormValues } from "@/models/proposalForm.model";
import { cepFormatter } from "@/utils/cepFormatter";
import { statesArray } from "@/utils/statesArray";
import { getAddressViaCep } from "@/services/viaCep.service";
import { useState } from "react";

interface AddressStepProps {
  formik: FormikProps<FormValues["address"]>;
  handleBack: () => void;
}

export default function AddressStep({ formik, handleBack }: AddressStepProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(e.target.name, e.target.value.toUpperCase());
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedNumber = e.target.value.replace(/\D/g, "");
    formik.setFieldValue("streetNumber", formattedNumber);
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedCep = cepFormatter(e.target.value);
    formik.setFieldValue("cep", formattedCep);
  };

  const handleCepSearchClick = async () => {
    const cleanCep = formik.values.cep
      ? formik.values.cep.replace("-", "")
      : "";
    if (cleanCep.length === 8) {
      setIsLoading(true);
      try {
        const data = await getAddressViaCep(cleanCep);

        if (!data.erro) {
          formik.setFieldValue("street", data.logradouro.toUpperCase());
          formik.setFieldValue("district", data.bairro.toUpperCase());
          formik.setFieldValue("city", data.localidade.toUpperCase());
          formik.setFieldValue("state", data.uf);
        } else {
          console.error("CEP não encontrado");
        }
      } catch (error) {
        console.error("Erro ao buscar o CEP", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.error("CEP inválido");
    }
  };

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="25px"
      boxShadow="md"
      borderColor="gray.200"
      maxW="800px"
      mx="auto"
      mt={6}
    >
      <Flex align="center" mb={4}>
        <Icon as={MdHome} boxSize={8} color="gray.500" mr={2} />
        <Box>
          <Heading size="md" color="gray.700" fontFamily="Lexend Deca">
            Seu Endereço
          </Heading>
        </Box>
      </Flex>
      <Divider mb={6} />
      <Box mb={6}>
        <Flex mb={4} gap="2rem" width="100%">
          <FormControl
            isInvalid={!!formik.errors?.cep && !!formik.touched?.cep}
          >
            <FormLabel fontFamily="Lexend Deca" htmlFor="cep">
              CEP
            </FormLabel>
            <Flex
              mb={2}
              gap={2}
              width="100%"
              flexDirection={{ base: "column", md: "row" }}
            >
              <InputGroup width={{ base: "100%", md: "30%" }}>
                <Field
                  size={"sm"}
                  as={Input}
                  id="cep"
                  name="cep"
                  fontFamily="Lexend Deca"
                  placeholder="Digite o CEP"
                  type="tel"
                  variant="flushed"
                  value={formik.values.cep}
                  onChange={handleCepChange}
                  maxLength={9}
                />
              </InputGroup>
              <Button
                px={{ base: "4", md: "6" }}
                mb={{ base: 2, md: 0 }}
                size="sm"
                onClick={handleCepSearchClick}
                isLoading={isLoading}
                bg="secondary"
                _hover={{ bg: "blue.600" }}
                color="white"
                w={{ base: "100%", md: "auto" }}
                leftIcon={<Icon as={MdSearch} />}
              >
                Consultar CEP
              </Button>
            </Flex>
            <FormErrorMessage>{formik.errors?.cep}</FormErrorMessage>
          </FormControl>
        </Flex>

        <Flex mb={4} gap="2rem" width="100%">
          <FormControl
            isInvalid={!!formik.errors?.street && !!formik.touched?.street}
          >
            <FormLabel fontFamily="Lexend Deca" htmlFor="street">
              Endereço
            </FormLabel>
            <Field
              as={Input}
              id="street"
              fontFamily="Lexend Deca"
              name="street"
              placeholder="Digite o nome da rua"
              type="text"
              variant="flushed"
              onChange={handleInputChange}
              w="100%"
            />
            <FormErrorMessage>{formik.errors?.street}</FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex mb={4} gap="2rem" width="100%">
          <FormControl
            isInvalid={
              Boolean(formik.errors?.streetNumber) &&
              !Boolean(formik.values.streetNumber)
            }
          >
            <FormLabel fontFamily="Lexend Deca" htmlFor="streetNumber">
              Número
            </FormLabel>
            <Field
              as={Input}
              fontFamily="Lexend Deca"
              id="streetNumber"
              name="streetNumber"
              placeholder="Digite o número"
              type="tel"
              variant="flushed"
              value={formik.values.streetNumber}
              onChange={handleNumberChange}
              w="100%"
            />
            <FormErrorMessage>O número precisa ser preenchido</FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex mb={4} gap="2rem" width="100%">
          <FormControl>
            <FormLabel fontFamily="Lexend Deca" htmlFor="complement">
              Complemento
            </FormLabel>
            <Field
              as={Input}
              fontFamily="Lexend Deca"
              id="complement"
              name="complement"
              placeholder="Digite o complemento (opcional)"
              type="text"
              variant="flushed"
              onChange={handleInputChange}
              w="100%"
            />
          </FormControl>
        </Flex>

        <Flex mb={4} gap="2rem" width="100%">
          <FormControl
            isInvalid={!!formik.errors?.district && !!formik.touched?.district}
          >
            <FormLabel fontFamily="Lexend Deca" htmlFor="district">
              Bairro
            </FormLabel>
            <Field
              as={Input}
              fontFamily="Lexend Deca"
              id="district"
              name="district"
              placeholder="Digite o bairro"
              type="text"
              variant="flushed"
              onChange={handleInputChange}
              w="100%"
            />
            <FormErrorMessage>{formik.errors?.district}</FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex mb={4} gap="2rem" width="100%">
          <FormControl
            isInvalid={!!formik.errors?.city && !!formik.touched?.city}
          >
            <FormLabel fontFamily="Lexend Deca" htmlFor="city">
              Cidade
            </FormLabel>
            <Field
              as={Input}
              fontFamily="Lexend Deca"
              id="city"
              name="city"
              placeholder="Digite a cidade"
              type="text"
              variant="flushed"
              onChange={handleInputChange}
              w="100%"
            />
            <FormErrorMessage>{formik.errors?.city}</FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex mb={4} gap="2rem" width="100%">
          <FormControl
            isInvalid={!!formik.errors?.state && !!formik.touched?.state}
          >
            <FormLabel fontFamily="Lexend Deca" htmlFor="state">
              Estado
            </FormLabel>
            <Field
              as={Select}
              id="state"
              name="state"
              mt={4}
              fontFamily="Lexend Deca"
              placeholder="Selecione o estado"
              variant={"flushed"}
              size="sm"
              color="black"
              _focus={{ boxShadow: "none", borderColor: "outline" }}
            >
              {statesArray.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Field>
            <FormErrorMessage>{formik.errors?.state}</FormErrorMessage>
          </FormControl>
        </Flex>
      </Box>

      <Flex fontFamily="Lexend Deca" justifyContent="flex-end" gap={3} mt={6}>
        <Button
          fontFamily="Lexend Deca"
          leftIcon={<Icon as={IoIosArrowBack} />}
          colorScheme="secondary"
          variant="outline"
          onClick={handleBack}
        >
          Voltar
        </Button>
        <Button
          fontFamily="Lexend Deca"
          rightIcon={<Icon as={IoIosArrowForward} />}
          colorScheme="primary"
          onClick={formik.submitForm}
        >
          Prosseguir
        </Button>
      </Flex>
    </Box>
  );
}
